import {
  initializeFaro,
  type BeforeSendHook,
  type Faro,
  type MetaPage,
} from '@grafana/faro-web-sdk';
import {
  isAuthConfigRegression,
  isClientNetworkAuthError,
  isSystemicAuthError,
  isUnclassifiedAuthError,
  normalizeAuthCode,
} from '@/lib/firebase-errors';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Business flows we can report errors for. Auth values are live today; adding a
 * new flow (payment, upload, …) is a one-line change here plus one Loki rule —
 * no new module and no new infrastructure. Keep this the ONLY place `reportAppError`
 * callers narrow to — the function itself stays flow-agnostic.
 */
export type AppFlow =
  | 'login'
  | 'signup'
  | 'saml'
  | 'custom-token'
  | 'mfa'
  | 'password-reset'
  | 'password-change'
  | 'enrollment'
  | 'magic-link'
  | 'reauth'
  // Staying logged in. A refresh failure silently un-authenticates an ACTIVE
  // session: Apollo drops the Authorization header, so the backend never even
  // receives a token and logs nothing. Without this flow the failure is
  // invisible on BOTH lanes. Keep in sync with the flow regex in ruler-rules.yaml.
  | 'token-refresh'
  // Payments. Keep in sync with the flow regex in logs-stack-v2 ruler-rules.yaml.
  | 'payment-plan-change'
  | 'payment-method'
  | 'payment-3ds'
  | 'payment-cancel'
  | 'payment-addon'
  | 'payment-subaccount'
  | 'payment-team'
  // Device pairing. Keep in sync with the flow regex in logs-stack-v2 ruler-rules.yaml.
  | 'pair-device-limit-check'
  | 'pair-device-submit'
  | 'pair-device-submit-deeplink'
  | 'pair-device-settle'
  // Playlists / feeds. Keep in sync with the flow regex in logs-stack-v2 ruler-rules.yaml.
  | 'playlist-feed'
  // File upload. `upload-options` is the query that gates the dialog: if it fails the upload
  // UI never opens. `upload-assembly` = the bytes (Uppy → Transloadit / Transforge),
  // `upload-save` = the saveAsset that follows, `upload-thumbnail` = the thumbnail-replace
  // variant. Keep in sync with the flow regex in logs-stack-v2 ruler-rules.yaml.
  | 'upload-options'
  | 'upload-assembly'
  | 'upload-save'
  | 'upload-thumbnail'
  // App-wide seams (transport wiring, auth bootstrap) — fire on ANY operation, not
  // just device pairing, so they are named neutrally rather than under `pair-device-*`.
  | 'app-transport'
  | 'app-bootstrap';

/**
 * `systemic` = the service is failing many users. `config_regression` = a deploy broke it for everyone.
 *
 * `unclassified` = a code that is neither a known expected user error nor a
 * known systemic one. Reported at low severity rather than dropped: dropping
 * the unknown is exactly how two live auth alerts shipped dead in Aug 2026 (the
 * code sets were derived from docs that did not match what the SDK emits).
 * A spike means "widen the sets", not "a user did something odd".
 */
export type AppErrorKind =
  | 'systemic'
  | 'config_regression'
  | 'unclassified'
  // The USER'S connectivity, not ours (dropped Wi-Fi, sleeping laptop, captive portal).
  // Kept as its own kind rather than dropped: one host failing is a client, many distinct
  // hosts failing together is us — and only a rule that groups by `host` can tell those
  // apart. Alert rules must EXCLUDE this kind from systemic/error-rate signals.
  | 'client_network'
  // The backend rejected something the UI was supposed to have already blocked
  // (screen limit, permission). Individually harmless; the RATE is the signal.
  // Alert rules must treat this as a guard-health signal, never as an outage.
  | 'guard_regression';

let faro: Faro | undefined;

/**
 * Attribute keys `reportAppError` ever sends. This is the ONLY set `beforeSend`
 * lets through on an event payload — see the Loki attribute-name contract below.
 *
 * `errType` (the error's constructor name) is additive and optional: it lets an
 * `unclassified` code be identified without a repro — the `error-code:-40` mystery
 * that prompted it was un-diagnosable from `code` alone. Bounded (a class name),
 * no PII (never the message). No alert rule selects on it, so adding it is safe.
 */
const ALLOWED_EVENT_ATTRIBUTE_KEYS = [
  'evt',
  'flow',
  'kind',
  'code',
  'host',
  'errType',
  // Attribution — a failure must be findable by the key the CONSUMER holds (rule 12 of the
  // structured-logging contract). Both are opaque ids, shape-gated below, never PII.
  'accountId',
  'assetId',
] as const;

/** Ids are opaque tokens. Anything else is dropped rather than emitted. */
const ID_SHAPE = /^[A-Za-z0-9_-]{1,64}$/;
function safeId(v: unknown): string | undefined {
  return typeof v === 'string' && ID_SHAPE.test(v) ? v : undefined;
}

/**
 * The signed-in tenant, read from the auth store so EVERY flow carries it for free. It is
 * the browser-lane analogue of the server lanes' `accountId`; the server line stays the
 * trusted one (this value is whatever the client holds). Absent when signed out.
 */
function currentAccountId(): string | undefined {
  try {
    const s = useAuthStore.getState();
    return safeId(s.company?._id ?? s.me?.accountId);
  } catch {
    return undefined;
  }
}

/**
 * The hostname the portal is being served from — for a white-label deployment
 * this is the customer's own domain (`signage.acme-corp.com`) or their subhost
 * (`acme.optisigns.net`), not ours.
 *
 * Without it, a config regression tells you "auth is broken" but not whether
 * that means globally or for ONE customer's domain — the first question anyone
 * asks. It is the browser-lane equivalent of `accountId` on the SAML lane.
 *
 * Hostname only: never the path, query or fragment (see `sanitizePageUrl` for
 * why those are unsafe). Stays in the log LINE, never a stream label —
 * customer domains are unbounded, and labelling them would reintroduce exactly
 * the cardinality blow-up this design avoids.
 */
function currentHost(): string {
  try {
    return typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Strips the query string AND fragment off any URL embedded in free-form
 * text. Firebase network-error messages sometimes echo the failing request
 * URL, which can carry API keys/tokens in its query params OR its fragment
 * (e.g. `#tok=SECRET`) — either must never reach Loki. (See `sanitizePageUrl`
 * for the stronger origin+pathname-only reduction used on `meta.page.url`.)
 */
function stripUrlQueryStrings(text: string): string {
  return text.replace(/(https?:\/\/[^\s?#]+)[?#][^\s]*/g, '$1');
}

/**
 * Reduces a page URL to origin + pathname, dropping BOTH the query string and
 * the fragment. `magic-link`, `password-reset`, and `custom-token` flows carry
 * Firebase one-time action tokens (`oobCode`, `continueUrl`, `apiKey`, …) in
 * either place. Returns undefined (caller drops the field) on anything
 * unparseable — never pass a partially-sanitised URL through.
 */
function sanitizePageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

/** Same page meta with `url` sanitised (or dropped on parse failure). Every
 * other field — `id`, `attributes` — passes through untouched. */
function sanitizePageMeta(page: MetaPage): MetaPage {
  const url = sanitizePageUrl(page.url);
  return url === undefined ? { id: page.id, attributes: page.attributes } : { ...page, url };
}

/**
 * Last line of defense before a payload leaves the browser.
 *
 * `item.meta` rides along on EVERY payload (event, exception, …) regardless of
 * type, and is populated by Faro's default page meta — `meta.page.url` is the
 * full `location.href`. This is NOT gated by `instrumentations: []` (that only
 * controls optional instrumentations, not the always-on default metas), so it
 * must be sanitised here to origin+pathname before anything else runs.
 *
 * Payload-level filtering (independent of what a future caller sends): event
 * attributes are hard-restricted to the 4 keys this module ever sets (never
 * email/password/token); exception `value` (message) and `context` (populated
 * from `error.cause`) have any embedded URL's query string stripped. We never
 * call pushLog/pushMeasurement/pushTraces and all instrumentations are
 * disabled, so those payload shapes should never reach this hook — if they
 * ever do, they pass through unmodified rather than risk breaking telemetry.
 *
 * Wrapped in try/catch as a second, redundant guard: Faro's own
 * pushEvent/pushError already wrap `transports.execute()` (which invokes this
 * hook) in a try/catch that swallows to `internalLogger.error`, and our own
 * `reportAppError`/`initFaro` wrap their entire bodies too — this hook must
 * never be the thing that breaks that chain.
 */
const beforeSend: BeforeSendHook = (item) => {
  try {
    if (item.meta.page) {
      item.meta.page = sanitizePageMeta(item.meta.page);
    }

    if ('attributes' in item.payload) {
      const attributes = item.payload.attributes;
      if (attributes) {
        const filtered: Record<string, string> = {};
        for (const key of ALLOWED_EVENT_ATTRIBUTE_KEYS) {
          const value = attributes[key];
          if (value !== undefined) filtered[key] = value;
        }
        item.payload.attributes = filtered;
      }
    } else if ('value' in item.payload) {
      item.payload.value = stripUrlQueryStrings(item.payload.value);
      const context = item.payload.context;
      if (context) {
        const strippedContext: Record<string, string> = {};
        for (const [key, value] of Object.entries(context)) {
          strippedContext[key] = stripUrlQueryStrings(value);
        }
        item.payload.context = strippedContext;
      }
    }

    return item;
  } catch {
    return item;
  }
};

/**
 * Initialise browser telemetry. No-ops unless VITE_FARO_URL is set, so local dev
 * and any unwired environment are completely unaffected.
 *
 * Instrumentation is deliberately NARROW: errors only. Faro's console/web-vitals/
 * view/session instrumentations are the bulk of its volume and none of them serve
 * error alerting — enabling them is how you accidentally multiply Loki spend.
 */
export function initFaro(): void {
  try {
    const url = import.meta.env.VITE_FARO_URL;
    const environment = import.meta.env.VITE_FARO_ENV;
    // BOTH are required — telemetry stays off unless the build says WHICH env it is.
    //
    // The `env` Loki label is derived from `environment` below, and the paging rules
    // select env="prod". An event with no environment could not be attributed, so the
    // safe outcome is to send nothing at all rather than emit unattributable data.
    // This also means a partial/accidental secret sync (URL present, env missing) into
    // a lower environment produces NO telemetry instead of ambiguous telemetry.
    if (!url || !environment || faro) return;
    faro = initializeFaro({
      url,
      apiKey: import.meta.env.VITE_FARO_API_KEY,
      app: {
        name: 'scio3-web',
        version: __APP_VERSION__,
        // Becomes `app_environment` on the wire, which Alloy promotes to the `env`
        // label. Deliberately NOT defaulted: see the guard above.
        environment,
      },
      // Errors only — see the note above.
      instrumentations: [],
      beforeSend,
    });
  } catch (err) {
    // Telemetry must never break app boot.
    console.error('Faro init failed', err);
  }
}

/**
 * Faro event names. `auth_error` is the shipped default — live alert rules select on it,
 * so it must stay the default for all existing auth call sites.
 */
export type AppEventName =
  | 'auth_error'
  | 'payment_error'
  | 'pair_device_fail'
  | 'app_error'
  | 'upload_error';

/**
 * User-visible journeys we time from the REAL user's gesture.
 *
 * DELIBERATELY ONLY THE HIGH-VOLUME TWO. RUM is a statistical instrument — it
 * needs a population to produce a proportion. app-load runs ~94k times a week on
 * app.optisigns.com and search is comparably common, so both yield stable
 * percentiles.
 *
 * The other three KPI journeys — pair, unpair, assign — are NOT here on purpose.
 * They fire a few hundred times a day, clustered in business hours and a handful
 * of geos, with no signal at all overnight. A RUM p95 off that sample looks
 * exactly like a real one and is noise; putting it on a panel beside these two is
 * how a metric gets trusted that shouldn't be. Those three are measured by the
 * SYNTHETIC journey canary (perf-kpi), which is their SLO input by design — see
 * its "WHY SYNTHETIC AND NOT RUM" header.
 *
 * Adding one of them here later is a real decision, not a completion: it needs
 * either the volume to have grown past roughly 1,000/day, or an explicit note
 * that it is a catastrophic-regression tripwire rather than a percentile.
 */
export type AppJourney = 'app-load' | 'search';

/**
 * Time a completed journey.
 *
 * Sent as a Faro MEASUREMENT (`type: 'journey'`, value `duration`) rather than an
 * event, because these are durations and measurements are what quantile_over_time
 * can unwrap in Loki. No Prometheus recording rule is required to query them —
 * that is only needed to project them into Prometheus-shaped series for alerting.
 *
 * Two rules for call sites, both learned the hard way in the synthetic canary:
 *   1. Start the clock at the user's GESTURE, not inside the handler. Input delay
 *      and framework scheduling are time the user waited.
 *   2. Stop it on SERVER CONFIRMATION, not on the dialog closing. Dialogs close
 *      optimistically, so timing to the close measures our own optimism.
 *
 * `outcome` keeps failed attempts out of the latency percentile while still
 * counting them — filter `outcome="ok"` when computing quantiles.
 */
export function reportJourney(p: {
  journey: AppJourney;
  ms: number;
  outcome: 'ok' | 'error';
  /**
   * Navigation Timing type ('navigate' | 'reload' | 'back_forward' | 'prerender').
   * Only meaningful for `app-load`: on an in-app route change performance.now() is
   * measured from the ORIGINAL document load, so without this the number silently
   * conflates a cold load with a warm one. Filter to navigate/reload when querying.
   */
  nav?: string;
  /**
   * Which navigation the clock was measured FROM: 'hard' = the document load,
   * 'soft' = a Chrome Soft Navigations timing origin (an in-app route change).
   *
   * `nav` above describes how the DOCUMENT was loaded and does not change when
   * the user moves around inside the app, so it cannot answer "was this a cold
   * load or a route change?". That gap is what made app-load report session age
   * (scio3#2024). Filter or split on this — never pool the two, they are
   * different journeys with different expectations.
   */
  navKind?: 'hard' | 'soft';
}): void {
  try {
    if (!faro) return;
    if (!Number.isFinite(p.ms) || p.ms < 0) return;
    faro.api.pushMeasurement(
      { type: 'journey', values: { duration: Math.round(p.ms) } },
      {
        context: {
          journey: p.journey,
          outcome: p.outcome,
          // Which tenant's domain this happened on — see currentHost().
          host: currentHost(),
          ...(p.nav ? { nav: p.nav } : {}),
          ...(p.navKind ? { navKind: p.navKind } : {}),
        },
      },
    );
  } catch {
    // Telemetry must never break a user flow.
  }
}

/**
 * The error's constructor name, if it is a safe bounded token. Never the message
 * (free text / PII); a class name only, shape-guarded so a hostile `constructor`
 * cannot smuggle arbitrary text into a Loki field. Returns undefined otherwise.
 */
function safeErrType(error: unknown): string | undefined {
  try {
    const name = (error as { constructor?: { name?: unknown } } | undefined)?.constructor?.name;
    return typeof name === 'string' && /^[A-Za-z0-9_$]{1,64}$/.test(name) ? name : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Report an app-level error that a human may need to act on.
 * Callers MUST pre-filter: only report SYSTEMIC failures, never expected user
 * errors — that filter is what keeps the alert channels urgent-only.
 *
 * Sends a `auth_error` event with plain attribute keys `evt`/`flow`/`kind`/`code`.
 * Alloy is what prefixes each key with `event_data_` on the way into Loki — do not
 * rename or restructure this payload, live alert rules depend on the resulting names.
 */
export function reportAppError(p: {
  flow: AppFlow;
  kind: AppErrorKind;
  code?: string;
  error?: unknown;
  /** Defaults to 'auth_error' — do not change that default, live rules depend on it. */
  evt?: AppEventName;
  /** The asset the failing request names (replace, thumbnail, …). Shape-gated; omit otherwise. */
  assetId?: string;
}): void {
  try {
    if (!faro) return;
    const evt = p.evt ?? 'auth_error';
    const errType = safeErrType(p.error);
    const accountId = currentAccountId();
    const assetId = safeId(p.assetId);
    faro.api.pushEvent(evt, {
      evt,
      flow: p.flow,
      kind: p.kind,
      code: p.code ?? 'unknown',
      // Which tenant's domain this happened on — see currentHost().
      host: currentHost(),
      // The error's constructor name — only when known. Helps identify unclassified codes.
      ...(errType ? { errType } : {}),
      // Attribution — see ALLOWED_EVENT_ATTRIBUTE_KEYS.
      ...(accountId ? { accountId } : {}),
      ...(assetId ? { assetId } : {}),
    });
    // A stack is only worth the bytes for the rare everyone-is-locked-out case.
    if (p.kind === 'config_regression' && p.error instanceof Error) {
      faro.api.pushError(p.error);
    }
  } catch {
    // Never propagate — telemetry must not break the auth UI.
  }
}

/**
 * Classify a caught Firebase auth error and report it ONLY if systemic.
 * Safe to call in every auth catch block — expected user errors are dropped here.
 *
 * The whole body is wrapped in try/catch: this is called at all 25 auth call
 * sites BEFORE any guard runs, and `(err as {code?:string})?.code` is a property
 * read on caller-supplied input — a throwing `code` getter must never propagate
 * into the auth UI. Telemetry must never break the thing it's observing.
 */
export function reportAuthErrorIfSystemic(flow: AppFlow, err: unknown): void {
  try {
    const code = normalizeAuthCode((err as { code?: string } | undefined)?.code);
    if (!code) return;

    // Checked BEFORE systemic: these codes are deliberately no longer IN the systemic
    // set, so this branch is what keeps them out of `unclassified` too.
    if (isClientNetworkAuthError(code)) {
      reportAppError({ flow, kind: 'client_network', code, error: err });
      return;
    }

    if (isSystemicAuthError(code)) {
      reportAppError({
        flow,
        kind: isAuthConfigRegression(code) ? 'config_regression' : 'systemic',
        code,
        error: err,
      });
      return;
    }

    // Neither known-expected nor known-systemic: the SDK emitted something our
    // taxonomy does not cover. Report it at low severity rather than dropping
    // it — a silent unknown is how the wrong-API-key signal went undetected.
    if (isUnclassifiedAuthError(code)) {
      // Pass the error so `errType` is captured — the constructor name is what turns
      // a mystery code (e.g. `error-code:-40`) into something identifiable.
      reportAppError({ flow, kind: 'unclassified', code, error: err });
    }
  } catch {
    // Never propagate — telemetry must not break the auth UI.
  }
}
